import axios from 'axios'
import type { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getConfig, ConfigKeys } from '@/common/config'
import { useAuthStore } from '@/stores/auth'
import { useStorage } from '@vueuse/core'

const apiClient = axios.create({
  baseURL: getConfig(ConfigKeys.API_BASE_URL) || 'http://localhost:3000',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

const getAuthStore = () => {
  return useAuthStore()
}

// Request interceptor for auth token and language
apiClient.interceptors.request.use((config) => {
  const token = getAuthStore().accessToken
  if (token) config.headers.Authorization = `Bearer ${token}`

  // Add Accept-Language header based on current locale
  const locale = useStorage('locale', 'en-US')
  if (locale.value) {
    config.headers['Accept-Language'] = locale.value
  }

  return config
})

// Simple retry counter to prevent infinite refresh loops
const MAX_RETRY_ATTEMPTS = 3
let isRefreshing = false

const logout = () => {
  const authStore = getAuthStore()
  authStore.logout()
}

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retryCount?: number
    }

    // Initialize retry count
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0
    }

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && originalRequest._retryCount < MAX_RETRY_ATTEMPTS) {
      // If already refreshing, wait and then reject to avoid race conditions
      if (isRefreshing) {
        return Promise.reject(new Error('Token refresh in progress'))
      }

      originalRequest._retryCount++
      isRefreshing = true

      try {
        const authStore = getAuthStore()
        const credential = authStore.getCredential()

        if (!credential) {
          console.warn('No credential available for token refresh')
          logout()
          return Promise.reject(new Error('No credential available'))
        }

        // Import the auth service dynamically to avoid circular dependencies
        const { loginWithCredential } = await import('@/modules/auth/services/auth_service')

        // Attempt to refresh the token
        const loginResponse = await loginWithCredential(credential)

        // Update the authorization header for the retry
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${loginResponse.access_token}`
        }

        // Retry the original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        logout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // If we've exceeded retry attempts or it's not a 401, reject the error
    if (error.response?.status === 401 && originalRequest._retryCount >= MAX_RETRY_ATTEMPTS) {
      console.warn('Max retry attempts exceeded, logging out')
      logout()
    }

    return Promise.reject(error)
  },
)

export default apiClient
